import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { getInitiatives, getInitiativeCounts } from "../../redux/initiatives";
import { getAllUsersTeamsListToUseInAllSections } from "../../redux/users/getAllUsersListToUseInAllSections";
import CreateInitiativeModal from "./components/CreateInitiativeModal";
import moment from "moment";

const statusColors = {
  future: { bg: "#E3F2FD", color: "#1565C0" },
  assigned: { bg: "#FFF3E0", color: "#E65100" },
  working: { bg: "#E8F5E9", color: "#2E7D32" },
  completed: { bg: "#E0F2F1", color: "#00695C" },
  cancelled: { bg: "#FFEBEE", color: "#C62828" },
};

const priorityColors = {
  low: { bg: "#E8F5E9", color: "#2E7D32" },
  medium: { bg: "#FFF3E0", color: "#E65100" },
  high: { bg: "#FFEBEE", color: "#C62828" },
  critical: { bg: "#F3E5F5", color: "#7B1FA2" },
};

const InitiativesList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, counts } = useSelector((state) => state.Initiatives);
  const { storedListOfUsers } = useSelector((state) => state.users);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  useEffect(() => {
    dispatch(getInitiatives({ 
      page: paginationModel.page + 1, 
      limit: paginationModel.pageSize,
      status: statusFilter, 
      priority: priorityFilter, 
      search 
    }));
    dispatch(getInitiativeCounts());
    dispatch(getAllUsersTeamsListToUseInAllSections());
  }, [dispatch, statusFilter, priorityFilter, paginationModel]);

  const handleSearch = () => {
    setPaginationModel({ ...paginationModel, page: 0 });
    dispatch(getInitiatives({ page: 1, limit: paginationModel.pageSize, status: statusFilter, priority: priorityFilter, search }));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const columns = [
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography 
          sx={{ 
            fontWeight: 500, 
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" }
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: statusColors[params.value]?.bg,
            color: statusColors[params.value]?.color,
            textTransform: "capitalize",
          }}
        />
      ),
    },
    {
      field: "priority",
      headerName: "Priority",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: priorityColors[params.value]?.bg,
            color: priorityColors[params.value]?.color,
            textTransform: "capitalize",
          }}
        />
      ),
    },
    {
      field: "owner",
      headerName: "Owner",
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            src={params.value?.profile_images?.profile_img_thumbnail}
            sx={{ width: 28, height: 28 }}
          >
            {params.value?.first_name?.[0]}
          </Avatar>
          <Typography variant="body2">{params.value?.full_name}</Typography>
        </Box>
      ),
    },
    {
      field: "milestones",
      headerName: "Progress",
      width: 100,
      renderCell: (params) => {
        const milestones = params.value || [];
        if (milestones.length === 0) return <Typography variant="body2" color="text.secondary">-</Typography>;
        const completed = milestones.filter((m) => m.is_completed).length;
        return (
          <Typography variant="body2">
            {completed}/{milestones.length}
          </Typography>
        );
      },
    },
    {
      field: "objective_completion_date",
      headerName: "Due Date",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value ? moment(params.value).format("MMM D, YYYY") : "-"}
        </Typography>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {moment(params.value).format("MMM D, YYYY")}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" fontWeight="600">
          Initiatives
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
        >
          New Initiative
        </Button>
      </Box>

      {/* Status Counts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.entries(statusColors).map(([status, colors]) => (
          <Grid item xs={12} sm={6} md={2.4} key={status}>
            <Card
              sx={{
                cursor: "pointer",
                border: statusFilter === status ? "2px solid #1976d2" : "1px solid #e0e0e0",
                "&:hover": { boxShadow: 2 },
              }}
              onClick={() => setStatusFilter(statusFilter === status ? "" : status)}
            >
              <CardContent sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                  {status}
                </Typography>
                <Typography variant="h5" fontWeight="600">
                  {counts.data?.[status] || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField
          placeholder="Search initiatives..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyPress={handleKeyPress}
          size="small"
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Priority</InputLabel>
          <Select
            value={priorityFilter}
            label="Priority"
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={handleSearch}>
          Apply
        </Button>
      </Box>

      {/* Error */}
      {list.errMsg && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="error">{list.errMsg}</Typography>
        </Box>
      )}

      {/* DataGrid */}
      <Box sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={list.data || []}
          columns={columns}
          loading={list.isLoading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          rowCount={list.pagination?.total || 0}
          paginationMode="server"
          onRowClick={(params) => navigate(`/initiatives/${params.id}`)}
          sx={{
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
          disableRowSelectionOnClick
        />
      </Box>

      {/* Empty State */}
      {!list.isLoading && (!list.data || list.data.length === 0) && !list.errMsg && (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography variant="h6" color="text.secondary">
            No initiatives found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create your first initiative to get started
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateModalOpen(true)}>
            Create Initiative
          </Button>
        </Box>
      )}

      {/* Create Modal */}
      <CreateInitiativeModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        users={(storedListOfUsers?.list || []).filter(
          (user) => user.role?.name === "Admin" || user.role?.name === "Team Leader"
        )}
      />
    </Box>
  );
};

export default InitiativesList;
